// components/layout/Sidebar.js
// Sidebar navigation — 3 responsive modes:
// Desktop >1200px: full 260px (text + icons)
// Tablet 768-1200px: icons only 60px
// Mobile <768px: hidden (controlled by Header hamburger via Drawer)

import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import TimelineIcon from '@mui/icons-material/TimelineOutlined';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import CategoryIcon from '@mui/icons-material/CategoryOutlined';
import LabelIcon from '@mui/icons-material/LabelOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// Navigation config
// =============================================================================
const MAIN_NAV = [
  { key: 'dashboard', path: '/dashboard', icon: DashboardIcon, labelKey: 'nav.dashboard' },
  { key: 'dataEntry', path: '/data-entry', icon: EditNoteIcon, labelKey: 'nav.dataEntry' },
  { key: 'metricHistory', path: '/metric-history', icon: TimelineIcon, labelKey: 'nav.metricHistory' },
  { key: 'reports', path: '/reports', icon: AssessmentIcon, labelKey: 'nav.reports' },
];

const ADMIN_NAV = [
  { key: 'units', path: '/admin/units', icon: BusinessIcon, labelKey: 'nav.units' },
  { key: 'categories', path: '/admin/categories', icon: CategoryIcon, labelKey: 'nav.categories' },
  { key: 'classifications', path: '/admin/classifications', icon: LabelIcon, labelKey: 'nav.classifications' },
  { key: 'metricTypes', path: '/admin/metric-types', icon: TuneIcon, labelKey: 'nav.metricTypes' },
  { key: 'metrics', path: '/admin/metrics', icon: SpeedIcon, labelKey: 'nav.metrics' },
  { key: 'users', path: '/admin/users', icon: PeopleIcon, labelKey: 'nav.users' },
  { key: 'companySettings', path: '/admin/settings', icon: StorageIcon, labelKey: 'nav.companySettings' },
];

// =============================================================================
// Constants
// =============================================================================
const SIDEBAR_FULL_WIDTH = 260;
const SIDEBAR_ICON_WIDTH = 60;

export { SIDEBAR_FULL_WIDTH, SIDEBAR_ICON_WIDTH };

// =============================================================================
// Sidebar Content — shared between permanent and temporary modes
// =============================================================================
function SidebarContent({ collapsed, onNavigate }) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const sidebarTheme = theme.custom?.sidebar || {};

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    const label = t(item.labelKey);

    const button = (
      <ListItem key={item.key} disablePadding sx={{ mb: 0.3 }}>
        <ListItemButton
          data-testid={`sidebar__nav-item--${item.key}`}
          onClick={() => onNavigate(item.path)}
          selected={active}
          sx={{
            minHeight: 44,
            borderRadius: 2,
            mx: collapsed ? 0.5 : 1,
            px: collapsed ? 1.5 : 2,
            justifyContent: collapsed ? 'center' : 'flex-start',
            bgcolor: active ? (sidebarTheme.activeItemBg || 'rgba(255,255,255,0.15)') : 'transparent',
            borderRight: active ? `3px solid ${sidebarTheme.activeItemBorder || '#fff'}` : '3px solid transparent',
            color: active ? (sidebarTheme.activeTextColor || '#fff') : (sidebarTheme.textColor || 'rgba(255,255,255,0.8)'),
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: sidebarTheme.hoverBg || 'rgba(255,255,255,0.08)',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 36,
              justifyContent: 'center',
              color: active ? (sidebarTheme.activeIconColor || '#fff') : (sidebarTheme.iconColor || 'rgba(255,255,255,0.6)'),
            }}
          >
            <Icon fontSize="small" />
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: active ? 600 : 400,
                noWrap: true,
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );

    // Tooltip for collapsed mode
    if (collapsed) {
      return (
        <Tooltip key={item.key} title={label} placement="left" arrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Logo / Company Name */}
      <Box
        data-testid="sidebar__logo"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          height: 64,
          px: collapsed ? 1 : 2.5,
          flexShrink: 0,
        }}
      >
        {collapsed ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: sidebarTheme.activeTextColor || '#fff',
              fontFamily: "'Rubik', sans-serif",
            }}
          >
            M
          </Typography>
        ) : (
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontWeight: 700,
              color: sidebarTheme.activeTextColor || '#fff',
              fontFamily: "'Rubik', sans-serif",
              letterSpacing: '0.5px',
            }}
          >
            MADAD
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 1 }} />

      {/* Main navigation */}
      <List sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
        {MAIN_NAV.map(renderNavItem)}

        {/* Admin section — only for admin users */}
        {isAdmin && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1, mx: collapsed ? 0.5 : 2 }} />

            {!collapsed && (
              <Typography
                variant="overline"
                sx={{
                  px: 2.5,
                  py: 0.5,
                  display: 'block',
                  color: sidebarTheme.iconColor || 'rgba(255,255,255,0.5)',
                  fontSize: '0.65rem',
                }}
              >
                {t('nav.admin')}
              </Typography>
            )}

            {ADMIN_NAV.map(renderNavItem)}
          </>
        )}
      </List>

      {/* Version footer */}
      {!collapsed && (
        <Box sx={{ p: 2, textAlign: 'center', flexShrink: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: sidebarTheme.iconColor || 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}
          >
            {t('common.version')} 0.2.03
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// =============================================================================
// Sidebar Component
// =============================================================================
export default function Sidebar({ mobileOpen, onMobileClose }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const sidebarTheme = theme.custom?.sidebar || {};

  // Responsive breakpoints
  const isDesktop = useMediaQuery('(min-width:1200px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1199px)');
  const isMobile = useMediaQuery('(max-width:767px)');

  const collapsed = isTablet;
  const currentWidth = collapsed ? SIDEBAR_ICON_WIDTH : SIDEBAR_FULL_WIDTH;

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      onMobileClose();
    }
  };

  // Sidebar background style
  const sidebarBg = useMemo(() => ({
    background: sidebarTheme.background || 'linear-gradient(180deg, #1565c0 0%, #0d47a1 100%)',
    ...(theme.custom?.glassmorphism?.sidebar || {}),
  }), [sidebarTheme.background, theme.custom?.glassmorphism?.sidebar]);

  // Desktop/Tablet — permanent sidebar
  const permanentDrawer = (
    <Drawer
      data-testid="sidebar__drawer--permanent"
      variant="permanent"
      anchor="right"
      sx={{
        display: { xs: 'none', sm: 'block' },
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          border: 'none',
          ...sidebarBg,
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <SidebarContent collapsed={collapsed} onNavigate={handleNavigate} />
    </Drawer>
  );

  // Mobile — temporary drawer
  const mobileDrawer = (
    <Drawer
      data-testid="sidebar__drawer--mobile"
      variant="temporary"
      anchor="right"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', sm: 'none' },
        '& .MuiDrawer-paper': {
          width: SIDEBAR_FULL_WIDTH,
          boxSizing: 'border-box',
          ...sidebarBg,
        },
      }}
    >
      <SidebarContent collapsed={false} onNavigate={handleNavigate} />
    </Drawer>
  );

  return (
    <>
      {(isDesktop || isTablet) && permanentDrawer}
      {isMobile && mobileDrawer}
    </>
  );
}
